import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TrophyIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../utils/bookingApi';
import { useFeature } from '../../hooks/useFeature';
import { PageHeader, Skeleton, EmptyState, ErrorState } from '../../components/ui';
import { formatDayDate } from '../../utils/dates';
import './booking-shared.css';

const STATUS_LABELS = {
  INVITED: { label: 'Action needed', tone: 'bad' },
  PAYMENT_PENDING: { label: 'Payment due', tone: 'bad' },
  PAID: { label: 'Entered', tone: 'good' },
  DECLINED: { label: 'Declined', tone: 'off' },
  WAIVED: { label: 'Entered (free)', tone: 'good' },
  // Taken out by the club after paying (status DECLINED)
  WITHDRAWN: { label: 'Withdrawn', tone: 'off' },
};

const money = (pence) => `£${(pence / 100).toFixed(2)}`;

export default function MyCompetitions() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const requirementsOn = useFeature('requirements');

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    bookingApi.getMyCompetitionEntries()
      .then(res => setEntries(res.data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const upcoming = entries.filter(e => new Date(e.competitionEvent.startDate) >= new Date());
  const past = entries.filter(e => new Date(e.competitionEvent.startDate) < new Date());

  const renderEntry = (entry) => {
    const s = (entry.withdrawnAt ? STATUS_LABELS.WITHDRAWN : STATUS_LABELS[entry.status]) || { label: entry.status, tone: 'off' };
    const ev = entry.competitionEvent;
    const isDeadlinePassed = new Date() > new Date(ev.entryDeadline);
    const canRespond = entry.status === 'INVITED' && (!isDeadlinePassed || ev.lateEntryFee !== null);
    const canPay = entry.status === 'PAYMENT_PENDING';

    return (
      <div key={entry.id} className="bk-card" style={{ marginBottom: '0.75rem' }}>
        <div className="bk-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{ fontWeight: 600, margin: '0 0 0.25rem' }}>{ev.name}</p>
            <p className="bk-small" style={{ margin: '0 0 0.25rem' }}>
              {ev.location} · {formatDayDate(ev.startDate)}
            </p>
            <p style={{ margin: 0, fontSize: 'var(--text-body)' }}>
              {entry.gymnast.firstName} {entry.gymnast.lastName}
            </p>
            {entry.categories.length > 0 && (
              <p className="bk-small" style={{ margin: '0.25rem 0 0' }}>
                {entry.categories.map(ec => ec.category.name).join(', ')}
              </p>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <span className={`bk-pill bk-pill--${s.tone}`}>{s.label}</span>
            {entry.totalAmount && <span className="bk-small" style={{ display: 'block', marginTop: '0.25rem' }}>{money(entry.totalAmount)}</span>}
            {entry.status === 'PAYMENT_PENDING' && entry.amountPaid > 0 && (
              <span className="bk-small" style={{ display: 'block' }}>
                {money(entry.totalAmount - entry.amountPaid)} still to pay
              </span>
            )}
          </div>
        </div>
        {(canRespond || canPay) && (
          <div className="bk-row-actions">
            <Link to={`/booking/competitions/${entry.id}/enter`} className="bk-btn bk-btn--primary bk-btn--sm">
              {canPay ? 'Pay now' : 'View invite'}
            </Link>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader
        title="Competitions"
        action={requirementsOn ? { label: 'Requirements', to: '/booking/requirements' } : undefined}
      />
      {loading ? (
        <Skeleton rows={3} label="Loading competitions…" />
      ) : failed ? (
        <ErrorState title="Couldn't load competitions" onRetry={load} />
      ) : entries.length === 0 ? (
        <EmptyState icon={TrophyIcon} title="No competition invitations yet" text="When the club invites your gymnast to a competition, it shows here." />
      ) : (
        <>
          {upcoming.length > 0 && (
            <>
              <h2 className="bk-section-title">Upcoming</h2>
              {upcoming.map(renderEntry)}
            </>
          )}
          {past.length > 0 && (
            <>
              <h2 className="bk-section-title" style={{ marginTop: '1.5rem' }}>Past</h2>
              {past.map(renderEntry)}
            </>
          )}
        </>
      )}
    </div>
  );
}
